export interface InvokeMessage {
  scope?: string;
  name: string;
  args?: Record<string, any>;
  uiArgs?: Record<string, any>;
  callbackId: number;
  innerArgs?: Record<string, any>;
  metrics?: Record<string, number>;
}

export function isRecord(value: any): value is Record<string, any> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

export function getSafeMessageContext(msg: string): Partial<InvokeMessage> {
  try {
    const parsed = typeof msg === 'string' ? JSON.parse(msg) : msg;
    if (!isRecord(parsed)) return {};
    return {
      ...(typeof parsed.name === 'string' && parsed.name ? { name: parsed.name } : {}),
      ...(typeof parsed.callbackId === 'number' && Number.isFinite(parsed.callbackId)
        ? { callbackId: parsed.callbackId } : {}),
      ...(isRecord(parsed.uiArgs) ? { uiArgs: parsed.uiArgs } : {}),
      ...(isRecord(parsed.innerArgs) ? { innerArgs: parsed.innerArgs } : {}),
      ...(isRecord(parsed.metrics) ? { metrics: parsed.metrics } : {}),
    };
  } catch (error) {
    return {};
  }
}

export function parseMessage(msg: string): InvokeMessage {
  const parsed = typeof msg === 'string' ? JSON.parse(msg) : msg;
  if (!isRecord(parsed)) {
    throw new TypeError('KSI message must be an object');
  }
  if (typeof parsed.scope !== 'undefined' && typeof parsed.scope !== 'string') {
    throw new TypeError('KSI message.scope must be a string');
  }
  if (typeof parsed.name !== 'string' || !parsed.name) {
    throw new TypeError('KSI message.name must be a non-empty string');
  }
  if (typeof parsed.callbackId !== 'number' || !Number.isFinite(parsed.callbackId)) {
    throw new TypeError('KSI message.callbackId must be a finite number');
  }
  if (typeof parsed.args !== 'undefined' && !isRecord(parsed.args)) {
    throw new TypeError('KSI message.args must be an object');
  }
  ['uiArgs', 'innerArgs', 'metrics'].forEach((field) => {
    if (typeof parsed[field] !== 'undefined' && !isRecord(parsed[field])) {
      throw new TypeError(`KSI message.${field} must be an object`);
    }
  });
  return parsed as InvokeMessage;
}
